import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Admin.css';
import { assessmentAPI } from '../services/api';
import QuestionManagement from './QuestionManagement';

const Admin = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [activeTab, setActiveTab] = useState('assessments');

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await assessmentAPI.getAllAssessments();
      
      if (response.success) {
        setAssessments(response.data);
      } else {
        setError('Failed to load assessments');
      }
    } catch (err) {
      console.error('Error fetching assessments:', err);
      setError('Failed to load assessments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Needs Attention': return '#FF6B6B';
      case 'Developing': return '#FFA726';
      case 'Strong': return '#4CAF50';
      default: return '#9C4DF4';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const viewDetails = (assessment) => {
    setSelectedAssessment(assessment);
  };

  const closeDetails = () => {
    setSelectedAssessment(null);
  };

  if (isLoading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <h2>Loading assessments...</h2>
          <p>Please wait while we fetch the data.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <h2>Error Loading Assessments</h2>
          <p>{error}</p>
          <button onClick={fetchAssessments} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <div className="sidebar-header">
            <h2>Admin Panel</h2>
          </div>
          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'assessments' ? 'active' : ''}`}
              onClick={() => setActiveTab('assessments')}
            >
              <span className="nav-icon">📊</span>
              <span>Assessments</span>
            </button>
            <button 
              className={`nav-item ${activeTab === 'questions' ? 'active' : ''}`}
              onClick={() => setActiveTab('questions')}
            >
              <span className="nav-icon">❓</span>
              <span>Questions</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button onClick={() => navigate('/')} className="btn btn-secondary btn-block">
              ← Back to Home
            </button>
          </div>
        </aside>

        <main className="admin-main">
          {activeTab === 'assessments' ? (
            <>
              <div className="admin-header">
                <div className="admin-header-content">
                  <div>
                    <h1>Assessment Dashboard</h1>
                    <p>View and manage all culture assessments</p>
                  </div>
                  <div className="admin-actions">
                    <button onClick={fetchAssessments} className="btn btn-primary">
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

      <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{assessments.length}</h3>
            <p>Total Assessments</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <h3>{assessments.filter(a => a.cultureHealth === 'Strong').length}</h3>
            <p>Strong Culture</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <h3>{assessments.filter(a => a.cultureHealth === 'Developing').length}</h3>
            <p>Developing</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{assessments.filter(a => a.cultureHealth === 'Needs Attention').length}</h3>
            <p>Needs Attention</p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="assessments-table-container">
          <table className="assessments-table">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Industry</th>
                <th>Company Size</th>
                <th>Role</th>
                <th>Score</th>
                <th>Culture Health</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessments.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                    <p>No assessments found. Complete an assessment to see results here.</p>
                  </td>
                </tr>
              ) : (
                assessments.map((assessment) => (
                  <tr key={assessment._id}>
                    <td className="company-name">{assessment.companyName}</td>
                    <td>{assessment.industry}</td>
                    <td>{assessment.companySize}</td>
                    <td>{assessment.role}</td>
                    <td className="score">{assessment.totalScore}</td>
                    <td>
                      <span 
                        className="health-badge" 
                        style={{ backgroundColor: getHealthColor(assessment.cultureHealth) }}
                      >
                        {assessment.cultureHealth}
                      </span>
                    </td>
                    <td className="date">{formatDate(assessment.createdAt)}</td>
                    <td>
                      <button 
                        onClick={() => viewDetails(assessment)} 
                        className="btn-view"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedAssessment && (
        <div className="modal-overlay" onClick={closeDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assessment Details</h2>
              <button className="close-btn" onClick={closeDetails}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>Company Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Company Name:</label>
                    <span>{selectedAssessment.companyName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Industry:</label>
                    <span>{selectedAssessment.industry}</span>
                  </div>
                  <div className="detail-item">
                    <label>Company Size:</label>
                    <span>{selectedAssessment.companySize}</span>
                  </div>
                  <div className="detail-item">
                    <label>Role:</label>
                    <span>{selectedAssessment.role}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Assessment Results</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Total Score:</label>
                    <span className="score-large">{selectedAssessment.totalScore}</span>
                  </div>
                  <div className="detail-item">
                    <label>Culture Health:</label>
                    <span 
                      className="health-badge large" 
                      style={{ backgroundColor: getHealthColor(selectedAssessment.cultureHealth) }}
                    >
                      {selectedAssessment.cultureHealth}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Assessment ID:</label>
                    <span className="id-text">{selectedAssessment._id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Submitted:</label>
                    <span>{formatDate(selectedAssessment.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Questions & Answers</h3>
                <div className="questions-list">
                  {selectedAssessment.questions && selectedAssessment.questions.map((q, index) => (
                    <div key={index} className="question-item">
                      <div className="question-header">
                        <span className="question-icon">{q.icon}</span>
                        <span className="question-text">{q.question}</span>
                      </div>
                      <div className="answer-text">{q.answer}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
            </>
          ) : (
            <QuestionManagement />
          )}
        </main>
      </div>
    </div>
  );
};

export default Admin;

